import React from 'react';
import {
    Box,
    Card,
    CardActionArea,
    CardContent,
    Typography,
    IconButton,
    useTheme,
    useMediaQuery,
    Tooltip
} from '@mui/material';
import {
    AddCircleOutline,
    Description,           // per "description"
    Info,                  // per "info"
    Speed,                 // per "speed"
    LightbulbOutlined,     // per "lightbulb"
    AddTask,               // per "add_task"
    Group,                 // per "group"
    NoteAdd,               // per "note_add"
    Tune,                  // per "tune"
    CheckCircle,           // per "check_circle"
    Star,                  // per "star"
    Flag,                  // per "flag"
    RocketLaunch,          // per "rocket_launch"
    Business,              // per "business"
    AccountTree,           // per "account_tree"
    Edit,                  // per "edit"
    PlayArrow,             // per "play_arrow"
    TaskAlt,               // per "task_alt"
    Monitor,            // per "monitoring"
    Analytics,             // per "analytics"
    Input,                 // per "input"
    Groups,                // per "groups"
    TipsAndUpdates,        // per "tips_and_updates"
    ChevronLeft,
    ChevronRight,
    AutoFixHigh,
} from '@mui/icons-material';

interface PromptSuggestion {
    id: string;
    title: string;
    description: string;
    prompt: string;
    icon: string;
    category: string;
}

interface PromptSuggestProps {
    onPromptSelect: (prompt: string) => void;
    suggestions?: PromptSuggestion[];
}

const iconMap: { [key: string]: React.ElementType } = {
    // Suggerimenti modello
    add_circle: AddCircleOutline,
    description: Description,
    info: Info,
    speed: Speed,
    lightbulb: LightbulbOutlined,
    add_task: AddTask,
    group: Group,
    note_add: NoteAdd,
    tune: Tune,
    check_circle: CheckCircle,
    star: Star,
    flag: Flag,

    // Suggerimenti dashboard
    rocket_launch: RocketLaunch,
    business: Business,
    account_tree: AccountTree,
    edit: Edit,
    play_arrow: PlayArrow,
    task_alt: TaskAlt,
    monitoring: Monitor,
    analytics: Analytics,
    input: Input,
    groups: Groups,
    tips_and_updates: TipsAndUpdates,

    // Fallback per icone non trovate
    magic: AutoFixHigh
};


const PromptSuggest: React.FC<PromptSuggestProps> = ({
    onPromptSelect,
    suggestions
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const scrollContainerRef = React.useRef<HTMLDivElement>(null);
    const [showLeftArrow, setShowLeftArrow] = React.useState(false);
    const [showRightArrow, setShowRightArrow] = React.useState(true);

    const handleScroll = (direction: 'left' | 'right') => {
        if (scrollContainerRef.current) {
            const scrollAmount = 300;
            const currentScroll = scrollContainerRef.current.scrollLeft;
            const targetScroll = direction === 'left'
                ? currentScroll - scrollAmount
                : currentScroll + scrollAmount;

            scrollContainerRef.current.scrollTo({
                left: targetScroll,
                behavior: 'smooth'
            });
        }
    };


    const checkScrollButtons = () => {
        if (scrollContainerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
            setShowLeftArrow(scrollLeft > 0);
            setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
        }
    };

    React.useEffect(() => {
        const container = scrollContainerRef.current;
        if (container) {
            container.addEventListener('scroll', checkScrollButtons);
            checkScrollButtons();
            return () => container.removeEventListener('scroll', checkScrollButtons);
        }
    }, [suggestions]);

    const handleCardClick = (prompt: string) => {
        onPromptSelect(prompt);
    };

    const getIcon = (iconName: string) => {
        const IconComponent = iconMap[iconName] || AutoFixHigh;
        return <IconComponent />;
    };

    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
                py: 2,
                px: { xs: 1, sm: 2 }
            }}
        >
            {/* Freccia sinistra */}
            {!isMobile && showLeftArrow && (
                <IconButton
                    onClick={() => handleScroll('left')}
                    sx={{
                        position: 'absolute',
                        left: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 100, // ✅ zIndex molto alto per stare sempre sopra
                        bgcolor: 'background.paper',
                        boxShadow: 2,
                        '&:hover': {
                           bgcolor: 'transparent',
                        }
                    }}
                >
                    <ChevronLeft />
                </IconButton>
            )}

            <Box
                ref={scrollContainerRef}
                sx={{
                    display: 'flex',
                    overflowX: 'auto',
                    overflowY: 'visible',
                    gap: 3,
                    pb: 2,
                    pt: 1,
                    px: 1,
                    scrollBehavior: 'smooth',
                    '&::-webkit-scrollbar': {
                        height: 8,
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: theme.palette.action.hover,
                        borderRadius: 4,
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: theme.palette.action.disabled,
                        borderRadius: 4,
                        '&:hover': {
                            backgroundColor: theme.palette.action.selected,
                        }
                    },
                    ...(isMobile && {
                        scrollbarWidth: 'none',
                        '&::-webkit-scrollbar': {
                            display: 'none',
                        }
                    })
                }}
            >
                {suggestions?.map((suggestion) => (
                    <Card
                        id={`suggestion-${suggestion.id}`}
                        key={suggestion.id}
                        sx={{
                            minWidth: { xs: 180, sm: 240 },
                            maxWidth: { xs: 180, sm: 240 },
                            height: 100,
                            flexShrink: 0,
                            background: theme.palette.mode === 'dark'
                                ? 'linear-gradient(145deg, #1e1e1e 0%, #2d2d2d 100%)'
                                : 'linear-gradient(145deg, #ffffff 0%, #f5f5f5 100%)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                transform: 'scale(1.05)',
                                boxShadow: theme.shadows[8],
                                zIndex: 10, // ✅ Manteniamo lo zIndex basso per le card
                            }
                        }}
                    >
                        <CardActionArea
                            onClick={() => handleCardClick(suggestion.prompt)}
                            sx={{ height: '100%' }}
                        >
                            <CardContent
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                    p: 2
                                }}
                            >
                                <Box>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            mb: 1
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 40,
                                                height: 40,
                                                borderRadius: 2,
                                                background: theme.palette.mode === 'dark'
                                                    ? 'rgba(255, 255, 255, 0.1)'
                                                    : 'rgba(0, 0, 0, 0.04)',
                                                color: theme.palette.primary.main,
                                                '& svg': {
                                                    fontSize: 18
                                                }
                                            }}
                                        >
                                            {getIcon(suggestion.icon)}
                                        </Box>
                                        <Typography
                                            variant="subtitle1"
                                            fontWeight={600}
                                            sx={{
                                                color: theme.palette.text.primary,
                                                fontSize: { xs: '0.85rem', sm: '0.90rem' }
                                            }}
                                        >
                                            {suggestion.title}
                                        </Typography>
                                    </Box>

                                    <Tooltip
                                        title={suggestion.description}
                                        placement="bottom-start"
                                        enterDelay={500}
                                        leaveDelay={200}
                                        disableInteractive={false}
                                        sx={{
                                            '& .MuiTooltip-tooltip': {
                                                maxWidth: 300,
                                                fontSize: '0.875rem',
                                            }
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: theme.palette.text.secondary,
                                                lineHeight: 1.4,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                fontSize: { xs: '0.75rem', sm: '0.80rem' },
                                                cursor: 'pointer'
                                            }}
                                        >
                                            {suggestion.description}
                                        </Typography>
                                    </Tooltip>
                                </Box>
                            </CardContent>
                        </CardActionArea>
                    </Card>
                ))}
            </Box>

            {/* Freccia destra */}
            {!isMobile && showRightArrow && (
                <IconButton
                    onClick={() => handleScroll('right')}
                    sx={{
                        position: 'absolute',
                        right: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 100, // ✅ zIndex molto alto per stare sempre sopra
                        bgcolor: 'background.paper',
                        boxShadow: 2,
                        '&:hover': {
                            bgcolor: 'transparent',
                        }
                    }}
                >
                    <ChevronRight />
                </IconButton>
            )}
        </Box>
    );
};

export default PromptSuggest;